import { queryOptions } from "@tanstack/react-query";
import { api, unwrap } from "@/client/lib/api";
import { dayBefore, daysOfMonth } from "@/features/attendance/rules";
import type {
  AttendanceEntry,
  AttendanceMonthRow,
  AttendanceRow,
  DayChange,
} from "@/features/attendance/schemas";

/**
 * Reads the day before too: a night shift checked in at 22:00 is still checked in after midnight,
 * and without it the screen would offer a second check-in the server refuses.
 */
export const myTodayQuery = (day: string) =>
  queryOptions({
    queryKey: ["attendance", "me", day],
    queryFn: (): Promise<AttendanceEntry[]> =>
      unwrap(
        api.attendance.history.$get({
          query: { person: "me", from: dayBefore(day), to: day },
        }),
      ).then(({ items }) => items),
  });

/**
 * An optimistic write needs an id and a stamp, and a day of work has neither: the person is its id
 * and its newest interval its stamp.
 */
export interface DayRow extends AttendanceRow {
  readonly id: string;
  readonly updatedAt: string;
}

export function dayRow(row: AttendanceRow): DayRow {
  return {
    ...row,
    id: row.personId,
    updatedAt: (row.entries ?? []).reduce(
      (latest, entry) => (entry.updatedAt > latest ? entry.updatedAt : latest),
      "",
    ),
  };
}

/** When they first checked in on that day, or null before they have. */
export function firstCheckIn(row: DayRow | undefined): string | null {
  return (
    (row?.entries ?? []).map((entry) => entry.checkedInAt).sort()[0] ?? null
  );
}

/** When the interval still open began; null once they have checked out, or before they came in. */
export function openSince(row: DayRow | undefined): string | null {
  return (
    (row?.entries ?? []).find((entry) => entry.checkedOutAt === null)
      ?.checkedInAt ?? null
  );
}

export const todayQuery = (day: string) =>
  queryOptions({
    queryKey: ["attendance", "day", day],
    queryFn: (): Promise<DayRow[]> =>
      unwrap(api.attendance.$get({ query: { day } })).then(({ items }) =>
        items.map(dayRow),
      ),
  });

/** A month row as the optimistic write reads it: the person is its id, its newest interval its stamp. */
export interface MonthRow extends AttendanceMonthRow {
  readonly id: string;
  readonly updatedAt: string;
}

export function monthRow(row: AttendanceMonthRow): MonthRow {
  return {
    ...row,
    id: row.personId,
    updatedAt: row.entries.reduce(
      (latest, entry) => (entry.updatedAt > latest ? entry.updatedAt : latest),
      "",
    ),
  };
}

/** One read per month, not thirty-one reads of the day's list. */
export const monthQuery = (month: string) =>
  queryOptions({
    queryKey: ["attendance", "month", month],
    queryFn: (): Promise<MonthRow[]> =>
      unwrap(api.attendance.month.$get({ query: { month } })).then(
        ({ items }) => items.map(monthRow),
      ),
  });

/** Every write to one person's day, oldest first, for the day panel's history. */
export const dayHistoryQuery = (person: string, day: string) =>
  queryOptions({
    queryKey: ["attendance", "day-history", person, day],
    queryFn: (): Promise<DayChange[]> =>
      unwrap(
        api.attendance["day-history"].$get({ query: { person, day } }),
      ).then(({ items }) => items),
  });

/** One person's approved leave. The type reaches a Manager or an Admin only. */
export interface LeaveSpan {
  readonly personId: string;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly leaveTypeId?: string;
  /** A half day is one day; the team month counts it as half. */
  readonly half?: "first" | "second" | null;
}

/** With Leave off nothing is asked for: a module that is off makes no request. */
export const leaveOffQuery = (from: string, to: string, leaveOn: boolean) =>
  queryOptions({
    queryKey: ["leave", "off", from, to, leaveOn],
    queryFn: (): Promise<LeaveSpan[]> =>
      leaveOn
        ? unwrap(api.leave.off.$get({ query: { from, to } })).then(
            ({ items }) => items,
          )
        : Promise.resolve([]),
  });

/** The caller's own check-outs the midnight close missed and nobody has set, latest day first. */
export const missedCheckOutsQuery = queryOptions({
  queryKey: ["attendance", "missed"],
  queryFn: (): Promise<AttendanceEntry[]> =>
    unwrap(api.attendance["missed-check-outs"].$get()).then(
      ({ items }) => items,
    ),
});
export function monthLeaveQuery(month: string, leaveOn: boolean) {
  const days = daysOfMonth(month);
  return leaveOffQuery(days[0] ?? month, days.at(-1) ?? month, leaveOn);
}

export interface LeaveTypeName {
  readonly id: string;
  readonly name: string;
}

/** The day names a person's leave by its type. With Leave off nothing is asked for. */
export const leaveTypesQuery = (leaveOn: boolean) =>
  queryOptions({
    queryKey: ["leave", "types", leaveOn],
    queryFn: (): Promise<LeaveTypeName[]> =>
      leaveOn
        ? unwrap(api.leave.types.$get()).then(({ items }) => items)
        : Promise.resolve([]),
  });
